import React, { useEffect, useMemo, useState } from "react";
import { Check, Loader2, ShieldCheck } from "lucide-react";

import {
  fetchAuthenticatedPrivateMediaBlob,
  getVisualQualityEvaluatorPreflightV1,
} from "../../lib/brandStudioApi";

export default function VisualQualityEvaluatorPreflightOwnerPanelV1() {
  const [status, setStatus] = useState("loading");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    let cancelled = false;
    getVisualQualityEvaluatorPreflightV1()
      .then((value) => {
        if (cancelled) return;
        setResult(value);
        setStatus("ready");
      })
      .catch((problem) => {
        if (cancelled) return;
        setError(
          problem?.response?.data?.detail ||
            "Не удалось открыть evaluator preflight.",
        );
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const preflight = result?.preflight;

  useEffect(() => {
    if (!preflight?.source_image_url) {
      setPreviewUrl("");
      return undefined;
    }
    let cancelled = false;
    let created = "";
    fetchAuthenticatedPrivateMediaBlob(preflight.source_image_url)
      .then((blob) => {
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setPreviewUrl(created);
      })
      .catch(() => {
        if (!cancelled) setPreviewUrl("");
      });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [preflight?.source_image_url]);

  const fingerprint = useMemo(
    () => (preflight?.preflight_fingerprint || "").slice(0, 12),
    [preflight?.preflight_fingerprint],
  );

  return (
    <section
      className="rounded-[24px] border border-dashed border-[#8C7359] bg-[#FFF8ED] p-5 md:p-7"
      data-testid="visual-quality-evaluator-preflight-owner-panel-v1"
    >
      <div className="text-[9.5px] uppercase tracking-[0.2em] text-[#8A6D53]">
        ПРОВЕРКА ВЛАДЕЛЬЦА · QG1.2R
      </div>
      <h3 className="mt-2 font-serif text-[27px] leading-tight text-[#2B211A]">
        Provider-neutral Evaluator Contract
      </h3>
      <p className="mt-2 max-w-3xl text-[12.5px] leading-5 text-[#655141]">
        Контракт проверки готов, но evaluator provider и model намеренно не
        выбраны. Изображение наружу не отправляется, платных вызовов нет,
        verdict не сохраняется.
      </p>

      {status === "loading" && (
        <div className="mt-5 inline-flex items-center gap-2 text-[12px] text-[#796652]">
          <Loader2 size={14} className="animate-spin" />
          Проверяю контракт…
        </div>
      )}

      {status === "error" && (
        <div className="mt-5 rounded-[13px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[12px] text-[#6B443A]">
          {String(error)}
        </div>
      )}

      {status === "ready" && result?.result !== "ready" && (
        <div className="mt-5 rounded-[13px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[12px] text-[#6B443A]">
          {result?.reason || "Evaluator contract недоступен."}
          {!!result?.blocking_reasons?.length && (
            <div className="mt-1 text-[10px]">
              Блокирует: {result.blocking_reasons.join(", ")}
            </div>
          )}
        </div>
      )}

      {preflight && (
        <div className="mt-6 space-y-4 border-t border-[#E2D5C3] pt-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF0DF] px-3 py-1.5 text-[10.5px] text-[#586044]">
              <Check size={12} />
              provider-neutral contract готов
            </span>
            <span className="text-[10.5px] text-[#8A745E]">
              QG1.2R · {fingerprint || "—"}
            </span>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#927A5B]">
                Evaluator
              </div>
              <div className="mt-2 text-[10.5px] leading-5 text-[#655141]">
                Provider: не выбран
                <br />
                Model: не выбрана
                <br />
                Endpoint: не выбран
                <br />
                Route: не назначен
                <br />
                Credentials: не проверяются
              </div>
            </article>

            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#927A5B]">
                Privacy
              </div>
              <div className="mt-2 text-[10.5px] leading-5 text-[#655141]">
                Binary egress: не разрешён
                <br />
                Reference images: не отправляются
                <br />
                Brand name / contacts: не отправляются
                <br />
                Provider privacy policy: ещё не выбрана
                <br />
                Retention / training: ещё не проверялись
              </div>
            </article>

            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#927A5B]">
                Cost safety
              </div>
              <div className="mt-2 text-[10.5px] leading-5 text-[#655141]">
                Provider pricing: ещё не выбрана
                <br />
                Estimate: недоступен до выбора provider
                <br />
                Hard anomaly cap: не назначен
                <br />
                Сейчас списание: $0
              </div>
            </article>
          </div>

          <article className="rounded-[16px] border border-[#DED0BC] bg-white p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] text-[#927A5B]">
              <ShieldCheck size={14} />
              Какое изображение будет проверяться
            </div>
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Текущий provider output для будущего Quality Gate evaluator"
                className="mt-3 max-h-[520px] w-full rounded-[12px] border border-[#E4D8C8] bg-[#F5F0E8] object-contain"
                data-testid="qg12-private-source-preview"
              />
            ) : (
              <div className="mt-3 rounded-[12px] bg-[#F5F0E8] px-4 py-8 text-center text-[10px] text-[#8A745E]">
                Закрытое изображение загружается…
              </div>
            )}
            <div className="mt-2 text-[9.5px] text-[#8A745E]">
              Source media: private · наружу на этом этапе не отправляется
            </div>
          </article>

          <article className="rounded-[16px] border border-[#DED0BC] bg-white p-4">
            <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#927A5B]">
              Минимизированный evaluator context
            </div>
            <div className="mt-3 text-[10px] leading-5 text-[#655141]">
              Workflow: {preflight.request_preview.minimized_context.workflow_type}
              <br />
              Common reject checks: {preflight.request_preview.minimized_context.common_reject_reasons.length}
              <br />
              Workflow requirements: {preflight.request_preview.minimized_context.workflow_requirements.length}
              <br />
              Reject conditions: {preflight.request_preview.minimized_context.workflow_reject_conditions.length}
              <br />
              Visual constraints: {preflight.request_preview.minimized_context.visual_constraints.length}
            </div>
          </article>

          <div className="rounded-[13px] border border-[#E0C4A6] bg-[#FFF4E8] px-4 py-3 text-[10.5px] leading-5 text-[#655141]">
            Live evaluation не включён. Provider/model/endpoint не выбраны,
            image egress отсутствует, provider calls = 0, spend = $0,
            verdict persisted = нет.
          </div>
        </div>
      )}
    </section>
  );
}
